// src/components/common/inline-status/InlineStatus.tsx
"use client";

import React, { useEffect, useMemo, useState } from "react";
import { Check, X, Info, AlertTriangle } from "lucide-react";
import "./InlineStatus.css";

type Kind = "success" | "error" | "info" | "warning";
type Pos = "top-left" | "top-right" | "bottom-left" | "bottom-right";

interface InlineStatusProps {
  kind: Kind;
  autoHideMs?: number; 
  onHide?: () => void; 
  position?: Pos; 
  size?: number; 
  title?: string; 
  className?: string;
}

export default function InlineStatus({
  kind,
  autoHideMs = 1600,
  onHide,
  position = "top-left",
  size = 26,
  title,
  className = "",
}: InlineStatusProps) {
  const [open, setOpen] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => {
      setOpen(false);
      onHide?.();
    }, autoHideMs);
    return () => clearTimeout(t);
  }, [autoHideMs, onHide]);

  const Icon = useMemo(() => {
    switch (kind) {
      case "success": return Check;
      case "error": return X;
      case "warning": return AlertTriangle;
      default: return Info;
    }
  }, [kind]);

  if (!open) return null;

  return (
    <div
      className={`inline-status inline-${position} inline-${kind} ${className}`}
      role="status"
      aria-live="polite"
      title={title}
      style={{ width: size, height: size }}
    >
      <Icon size={Math.max(14, size - 12)} />
      <span className="sr-only">
        {kind === "success" ? "Success" :
         kind === "error" ? "Error" :
         kind === "warning" ? "Warning" : "Information"}
      </span>
    </div>
  );
}
